import { z } from 'zod';
import { personNameSchema } from './person-name.js';
import { EmploymentStatusSchema } from '../enums/employment-status.js';
import { UserRoleSchema } from '../enums/user-role.js';
import { InvitationStatusSchema } from '../enums/invitation-status.js';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';

export const InvitationSchema = z.object({
  id: UuidV7Schema,
  email: z.string().email(),
  role: UserRoleSchema,
  partnerId: UuidV7Schema,
  managerId: UuidV7Schema.nullable(),
  superviseurId: UuidV7Schema.nullable(),
  // Pré-remplissage du profil au moment du sign-up (cf. accept() qui
  // s'en sert pour la création du User). Optionnels et nullables.
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  externalId: z.string().nullable().optional(),
  employmentStatus: EmploymentStatusSchema.nullable().optional(),
  invitedById: UuidV7Schema,
  status: InvitationStatusSchema,
  expiresAt: IsoDateStringSchema,
  acceptedAt: IsoDateStringSchema.nullable(),
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
  /**
   * Flag calculé côté serveur depuis le pattern d'email (cf.
   * isTemporaryInvitationEmail). Renvoyé pour que le front sache si
   * l'invitation est temporaire sans devoir re-parser l'email — utile
   * notamment côté admin où l'AnonymizeInterceptor masque le pattern
   * `tmp-…@invitation.doorly` derrière un matricule anonymisé.
   */
  isTemporary: z.boolean().optional(),
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const CreateInvitationSchema = z
  .object({
    /**
     * Email du destinataire. Optionnel : si vide ou absent, l'invitation
     * est créée en mode "temporaire" — aucun mail n'est envoyé, le back
     * génère un email placeholder (`tmp-…@invitation.doorly`) qui sera
     * remplacé par la vraie adresse via PATCH ultérieur. Permet à un RP
     * de pré-configurer son équipe (rôle/partner/manager/sup) avant
     * d'avoir collecté toutes les adresses réelles.
     */
    email: z.preprocess(
      (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
      z.string().email().toLowerCase().optional(),
    ),
    role: UserRoleSchema,
    partnerId: z.preprocess((v) => (v === '' ? null : v), UuidV7Schema.nullable().optional()),
    managerId: z.preprocess((v) => (v === '' ? null : v), UuidV7Schema.nullable().optional()),
    superviseurId: z.preprocess((v) => (v === '' ? null : v), UuidV7Schema.nullable().optional()),
    firstName: personNameSchema({ max: 100 }).optional(),
    lastName: personNameSchema({ max: 100 }).optional(),
    phone: z.string().max(30).optional(),
    externalId: z.string().max(50).optional(),
    /**
     * Chaîne de responsabilité — obligatoire à la création (typologie,
     * 15/09/2026), sauf pour un admin Hipto : la notion n'a de sens que dans un
     * réseau partenaire.
     */
    employmentStatus: EmploymentStatusSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.role !== 'admin' && !v.employmentStatus) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['employmentStatus'],
        message: 'Indiquez si la personne est salariée ou non.',
      });
    }
  });
export type CreateInvitationInput = z.infer<typeof CreateInvitationSchema>;

/**
 * Pattern d'email temporaire généré pour les invitations sans
 * destinataire réel. Le suffixe `.doorly` n'est pas un vrai TLD donc
 * pas de risque de collision avec une vraie adresse.
 */
export const TEMPORARY_EMAIL_DOMAIN = 'invitation.doorly';
export const TEMPORARY_EMAIL_REGEX = /^tmp-[0-9a-f]+@invitation\.doorly$/i;
export function isTemporaryInvitationEmail(email: string | null | undefined): boolean {
  return !!email && TEMPORARY_EMAIL_REGEX.test(email);
}

/**
 * Mise à jour d'une invitation `pending`. Inclut l'email — si l'email
 * change, le service révoque l'invitation Clerk existante et en crée
 * une nouvelle automatiquement (transparent pour le caller). Les autres
 * champs restent un simple PATCH sur la row Postgres.
 */
export const UpdateInvitationSchema = z.object({
  /**
   * Email destinataire. Vide ou absent = pas de changement (PATCH léger
   * sans toucher à Clerk). Une string vide explicite ("") = repasse en
   * temporaire — révoque l'invitation Clerk existante et génère un
   * placeholder. Une string non vide = rotation Clerk vers cette adresse.
   */
  email: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? '' : v),
    z.union([z.literal(''), z.string().email().toLowerCase()]).optional(),
  ),
  role: UserRoleSchema,
  partnerId: z.preprocess((v) => (v === '' ? null : v), UuidV7Schema.nullable().optional()),
  managerId: z.preprocess((v) => (v === '' ? null : v), UuidV7Schema.nullable().optional()),
  superviseurId: z.preprocess((v) => (v === '' ? null : v), UuidV7Schema.nullable().optional()),
  firstName: personNameSchema({ max: 100 }).optional(),
  lastName: personNameSchema({ max: 100 }).optional(),
  phone: z.string().max(30).optional(),
  externalId: z.string().max(50).optional(),
  /** Corrigeable tant que l'invitation est en attente. */
  employmentStatus: EmploymentStatusSchema.nullable().optional(),
});
export type UpdateInvitationInput = z.infer<typeof UpdateInvitationSchema>;

/**
 * Body optionnel pour `POST /invitations/:id/resend`. Sans `email`, on
 * renvoie l'invitation Clerk à l'adresse courante. Avec `email`, c'est
 * équivalent à un PATCH qui change l'email — combine resend + changement
 * d'adresse en une seule opération.
 */
export const ResendInvitationSchema = z.object({
  email: z.string().email().toLowerCase().optional(),
});
export type ResendInvitationInput = z.infer<typeof ResendInvitationSchema>;
